import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Instagram } from "lucide-react";
import Wordmark from "@/components/Wordmark";
import { useAuth } from "@/context/AuthContext";
import { HOME } from "@/constants/testIds";

const LINKS = [
  { to: "/how-it-works", label: "How It Works" },
  { to: "/services", label: "Services" },
  { to: "/selected-work", label: "Selected Work" },
  { to: "/journal", label: "Insights" },
  { to: "/create-with-us", label: "Create With Us", accent: true },
  { to: "/connect", label: "Contact Your Producer", live: true },
];

function UserAvatar({ user, size = "md" }) {
  const cls = size === "lg" ? "w-12 h-12" : "w-9 h-9 md:w-10 md:h-10";
  if (user?.picture) {
    return (
      <img
        src={user.picture}
        alt={user.name || user.email}
        className={`${cls} rounded-full object-cover border border-bnm`}
        referrerPolicy="no-referrer"
      />
    );
  }
  const letter = (user?.name || user?.email || "?").charAt(0).toUpperCase();
  return (
    <div className={`${cls} rounded-full bg-[#0a0a0a] text-white flex items-center justify-center ${size === "lg" ? "text-[16px]" : "text-[13px]"} font-medium border border-bnm`}>
      {letter}
    </div>
  );
}

export default function Nav() {
  const [open, setOpen] = useState(false);
  const { user, signIn, signOut } = useAuth();
  const { pathname } = useLocation();

  // When we're on the landing page (which owns the Discovery flow), clicking
  // the logo should not reset silently. We dispatch a custom event that
  // Landing.jsx listens for, so it can show a confirmation modal if the
  // client is mid-conversation. On any other page the link navigates normally.
  const onBrandClick = (e) => {
    if (pathname === "/") {
      e.preventDefault();
      window.dispatchEvent(new CustomEvent("bnm:home-request"));
    }
  };

  return (
    <>
      <header className="w-full border-b border-bnm bg-white/90 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 h-[78px] md:h-[92px] flex items-center justify-between">
          <Link
            to="/"
            onClick={onBrandClick}
            data-testid={HOME.navBrand}
            aria-label="Brand New Money Films, Home"
          >
            <Wordmark size="md" />
          </Link>

          <div className="flex items-center gap-2 md:gap-3">
            {user ? (
              <Link
                to="/projects"
                className="inline-flex items-center gap-2 md:gap-3 pl-1 pr-3 md:pr-4 py-1 rounded-full border border-bnm hover:border-black transition-colors"
                data-testid="nav-user"
              >
                <UserAvatar user={user} />
                <span className="text-[13px] md:text-[14px] text-ink hidden sm:inline">
                  {(user.name || user.email).split(" ")[0]}
                </span>
              </Link>
            ) : (
              <button
                type="button"
                onClick={signIn}
                data-testid={HOME.navSignIn}
                className="denim-surface denim-stitch text-[13px] md:text-[14px] px-4 md:px-5 py-2.5 md:py-3 rounded-full text-white transition-colors"
              >
                Sign in
              </button>
            )}
            <button
              type="button"
              aria-label="open menu"
              onClick={() => setOpen(true)}
              className="w-11 h-11 rounded-full border border-bnm bg-white flex items-center justify-center hover:border-black transition-colors"
              data-testid="nav-menu-toggle"
            >
              <Menu className="w-5 h-5 text-ink" />
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div
          className="fixed inset-0 z-[60] bg-white/60 backdrop-blur-sm animate-[fadeIn_250ms_ease-out_both]"
          onClick={() => setOpen(false)}
          data-testid="nav-menu-sheet"
        >
          <div
            className="absolute right-0 top-0 h-full w-[min(520px,92vw)] max-w-[520px] bg-white border-l border-bnm p-8 sm:p-12 flex flex-col overflow-y-auto animate-[bnm-drawer-in_380ms_cubic-bezier(0.22,1,0.36,1)_both]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <Wordmark size="sm" />
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="close menu"
                className="w-10 h-10 rounded-full border border-bnm flex items-center justify-center hover:border-black transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Account: the client's own space, not ours. */}
            {user ? (
              <Link
                to="/projects"
                onClick={() => setOpen(false)}
                className="mt-4 mb-8 md:mb-10 flex items-center gap-4 rounded-2xl border border-bnm p-4 hover:border-black transition-colors group"
                data-testid="nav-menu-account"
              >
                <UserAvatar user={user} size="lg" />
                <span className="flex flex-col min-w-0">
                  <span className="font-display font-bold text-[16px] text-ink leading-[1.25] truncate" data-testid="nav-menu-account-name">
                    {user.name || user.email.split("@")[0]}
                  </span>
                  <span className="text-[12.5px] text-muted-ink truncate" data-testid="nav-menu-account-email">
                    {user.email}
                  </span>
                </span>
              </Link>
            ) : (
              <p className="text-[12.5px] text-muted-ink leading-[1.6] mb-10 md:mb-12" data-testid="nav-menu-tagline">
                Made in India. Producing worldwide.
              </p>
            )}

            <nav className="flex flex-col gap-6 md:gap-7">
              {LINKS.map((l, i) => (
                <Link
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className={`font-display tracking-[-0.025em] text-[29px] md:text-[38px] font-extrabold transition-colors ${
                    pathname === l.to
                      ? "denim-surface denim-stitch rounded-full text-white px-7 py-2 self-start w-fit -ml-7"
                      : "text-ink hover:text-denim-bnm"
                  }`}
                  data-testid={`nav-menu-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <span
                    className={`eyebrow mr-3 align-middle ${
                      pathname === l.to
                        ? "text-white/60"
                        : l.accent
                          ? "text-[color:var(--bnm-red)]"
                          : "eyebrow-muted"
                    }`}
                  >
                    0{i + 1}
                  </span>
                  {l.label}
                  {l.accent && pathname !== l.to && (
                    <span
                      className="inline-block w-[7px] h-[7px] rounded-full bg-[color:var(--bnm-red)] ml-2.5 align-middle"
                      aria-hidden
                    />
                  )}
                  {l.live && pathname !== l.to && (
                    <span className="live-dot ml-2.5 align-middle" aria-hidden data-testid="nav-live-dot" />
                  )}
                </Link>
              ))}
              {user && (
                <Link
                  to="/projects"
                  onClick={() => setOpen(false)}
                  className={`font-display tracking-[-0.025em] text-[29px] md:text-[38px] font-extrabold ${
                    pathname === "/projects" ? "text-denim-bnm" : "text-ink hover:text-denim-bnm"
                  } transition-colors`}
                  data-testid="nav-menu-projects"
                >
                  <span className="eyebrow eyebrow-muted mr-3 align-middle">0{LINKS.length + 1}</span>
                  Your Projects
                </Link>
              )}
            </nav>

            <div className="mt-auto pt-16">
              {/* Studio information */}
              <div className="pb-6" data-testid="nav-menu-studio-info">
                <div className="eyebrow eyebrow-muted mb-1.5">Across India</div>
                <div className="text-[12px] text-ink leading-[1.7]">
                  Bengaluru • Mumbai • Delhi • Chennai • Hyderabad • Gujarat • Kerala • Rajasthan
                </div>
              </div>
              <hr className="denim-divider mb-6" />
              <div className="flex items-end justify-between gap-4 pb-2">
                <div className="flex flex-col gap-2.5">
                  <a
                    href="https://instagram.com/brandnewmoneyfilms"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-[13px] text-muted-ink hover:text-ink transition-colors"
                    data-testid="nav-menu-instagram"
                  >
                    <Instagram className="w-4 h-4" aria-hidden />
                    <span>@brandnewmoneyfilms</span>
                  </a>
                  <a
                    href="mailto:ida@brandnewmoneyfilms.com"
                    className="text-[13px] text-muted-ink hover:text-ink transition-colors"
                    data-testid="nav-menu-email"
                  >
                    ida@brandnewmoneyfilms.com
                  </a>
                </div>
                {user ? (
                  <button
                    type="button"
                    onClick={async () => {
                      await signOut();
                      setOpen(false);
                    }}
                    className="text-[13px] md:text-[14px] px-4 md:px-5 py-2.5 rounded-full border border-bnm text-ink hover:border-black transition-colors shrink-0"
                    data-testid="nav-menu-signout"
                  >
                    Sign out
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      signIn();
                      setOpen(false);
                    }}
                    className="denim-surface denim-stitch text-[13px] md:text-[14px] px-4 md:px-5 py-2.5 rounded-full text-white transition-colors shrink-0"
                    data-testid="nav-menu-signin"
                  >
                    Sign in
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
